import { defineNuxtPlugin } from 'nuxt/app'
import { setupDevtoolsPlugin } from '@vue/devtools-api'
import type { ComponentPublicInstance } from 'vue'

/**
 * Dev-only debugging aid: draws an outline and a small name label around the
 * root element of every mounted Vue component. Boxes live in a single fixed,
 * pointer-events-less overlay so component DOM and layout are never touched.
 *
 * Toggle with Ctrl+Shift+O (state persists in localStorage), or via the
 * "Component outlines" plugin settings in the Vue DevTools panel, which also
 * offers a name filter.
 */
const STORAGE_KEY = 'am3:component-outlines'

function componentName(instance: ComponentPublicInstance): string | null {
  const options = instance.$options as { name?: string, __name?: string, __file?: string }
  const fromFile = options.__file?.split('/').pop()?.replace(/\.vue$/, '')
  return options.name || options.__name || fromFile || null
}

function colorFor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 360
  return `hsl(${hash} 80% 45%)`
}

export default defineNuxtPlugin((nuxtApp) => {
  if (process.env.NODE_ENV !== 'development') return

  const instances = new Set<ComponentPublicInstance>()
  let enabled = true
  try {
    enabled = localStorage.getItem(STORAGE_KEY) !== 'off'
  }
  catch { /* storage unavailable */ }

  const overlay = document.createElement('div')
  overlay.setAttribute('data-component-outlines', '')
  overlay.style.cssText
    = 'position:fixed;inset:0;pointer-events:none;z-index:2147483000;overflow:hidden;'
  document.body.appendChild(overlay)

  const pool: HTMLDivElement[] = []

  // Settings from the Vue DevTools panel; read every frame so no event wiring is needed.
  let readSettings = () => ({ enabled: true, filter: '' })
  setupDevtoolsPlugin(
    {
      id: 'am3-component-outlines',
      label: 'Component outlines',
      packageName: 'audience-manager-3',
      homepage: 'https://vuejs.org',
      // Cast: the full App type makes TypeScript's instantiation depth blow up.
      app: nuxtApp.vueApp as never,
      settings: {
        enabled: { label: 'Show outlines', type: 'boolean', defaultValue: true },
        filter: { label: 'Only components matching', type: 'text', defaultValue: '' },
      },
    },
    (api) => {
      readSettings = () => api.getSettings() as { enabled: boolean, filter: string }
    },
  )

  function box(index: number): HTMLDivElement {
    let el = pool[index]
    if (!el) {
      el = document.createElement('div')
      el.style.cssText = 'position:absolute;box-sizing:border-box;border:1px dashed;'
      const label = document.createElement('span')
      label.style.cssText
        = 'position:absolute;top:0;left:0;padding:0 3px;font:9px/12px monospace;color:#fff;white-space:nowrap;max-width:100%;overflow:hidden;text-overflow:ellipsis;'
      el.appendChild(label)
      overlay.appendChild(el)
      pool[index] = el
    }
    return el
  }

  function draw() {
    let used = 0
    const settings = readSettings()
    const filter = settings.filter.trim().toLowerCase()
    if (enabled && settings.enabled) {
      for (const instance of instances) {
        const el = instance.$el as Node | null
        if (!(el instanceof Element) || !el.isConnected) continue
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) continue
        if (rect.bottom < 0 || rect.right < 0 || rect.top > innerHeight || rect.left > innerWidth) continue

        const name = componentName(instance)!
        if (filter && !name.toLowerCase().includes(filter)) continue
        const color = colorFor(name)
        const b = box(used++)
        b.style.display = 'block'
        b.style.borderColor = color
        b.style.transform = `translate(${rect.left}px,${rect.top}px)`
        b.style.width = `${rect.width}px`
        b.style.height = `${rect.height}px`
        const label = b.firstElementChild as HTMLSpanElement
        label.style.background = color
        if (label.textContent !== name) label.textContent = name
      }
    }
    for (let i = used; i < pool.length; i++) pool[i].style.display = 'none'
    requestAnimationFrame(draw)
  }
  requestAnimationFrame(draw)

  window.addEventListener('keydown', (e) => {
    // e.code: independent of layout and of Shift/Option changing e.key.
    if (e.ctrlKey && e.shiftKey && e.code === 'KeyO') {
      e.preventDefault()
      enabled = !enabled
      try {
        localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off')
      }
      catch { /* storage unavailable */ }
    }
  }, true) // capture, so nothing in the app can swallow the event first

  // The DevTools "Outlines" tab toggles by writing localStorage from its iframe.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) enabled = e.newValue !== 'off'
  })

  nuxtApp.vueApp.mixin({
    mounted(this: ComponentPublicInstance) {
      if (componentName(this)) instances.add(this)
    },
    beforeUnmount(this: ComponentPublicInstance) {
      instances.delete(this)
    },
  })
})
